import Seo from "@/components/Seo";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, List, X, Share2, Download, ExternalLink, TrendingUp, Info, Sparkles } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import api from "@/lib/api";
import NewsletterSignup from "@/components/NewsletterSignup";

const DATA_CREDIT_URL = "https://huggingface.co/datasets/Anthropic/EconomicIndex";

function pctColor(pct) {
  if (pct >= 50) return "#00F0FF";
  if (pct >= 30) return "#5e7bff";
  if (pct >= 15) return "#a78bfa";
  return "#4ade80";
}

function DataCredit() {
  return (
    <a href={DATA_CREDIT_URL} target="_blank" rel="noopener noreferrer" data-testid="data-credit"
      className="inline-flex items-center gap-1 text-xs text-[#94A3B8] hover:text-[#00F0FF] transition">
      Data: Anthropic Economic Index (CC-BY) <ExternalLink className="w-3 h-3" />
    </a>
  );
}

function ShareCard({ job, cardRef }) {
  return (
    <div ref={cardRef} data-testid="share-card"
      style={{ width: 600, background: "linear-gradient(160deg,#0B0F19,#111a2e)", color: "#fff" }}
      className="p-10 rounded-2xl border border-[#00F0FF]/25">
      <div className="flex items-center gap-2 mb-6">
        <Sparkles style={{ color: "#00F0FF" }} className="w-5 h-5" />
        <span style={{ color: "#00F0FF", letterSpacing: "0.1em" }} className="font-mono-tech text-xs uppercase">How much is AI doing in your job?</span>
      </div>
      <div className="text-2xl font-display font-bold leading-tight mb-6">{job.title}</div>
      <div style={{ color: pctColor(job.pct) }} className="font-display font-extrabold text-7xl leading-none">{job.pct}%</div>
      <div className="text-[#94A3B8] text-sm mt-2">of this job's tasks currently show AI use</div>
      <div className="mt-6 inline-flex items-center gap-2 rounded-full px-4 py-1.5" style={{ background: "rgba(0,240,255,0.12)", border: "1px solid rgba(0,240,255,0.35)" }}>
        <TrendingUp className="w-4 h-4" style={{ color: "#00F0FF" }} />
        <span className="text-sm">{job.pct === 0 ? "No AI use observed yet" : `Ranked #${job.rank} of ${job.total} by AI exposure`}</span>
      </div>
      <div className="flex items-center justify-between mt-8 pt-5" style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
        <span className="font-display font-bold text-lg" style={{ color: "#00F0FF" }}>worldaiinfo.com</span>
        <span className="text-[11px] text-[#64748B]">Data: Anthropic Economic Index (CC-BY)</span>
      </div>
    </div>
  );
}

function ResultCard({ job, onShare }) {
  const color = pctColor(job.pct);
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className="card-surface rounded-2xl p-6 md:p-8" data-testid="result-card">
      <h2 className="font-display font-bold text-2xl">{job.title}</h2>
      <div className="mt-6 flex flex-col sm:flex-row sm:items-end gap-2">
        <span className="font-display font-extrabold text-6xl md:text-7xl leading-none" style={{ color }} data-testid="result-pct">{job.pct}%</span>
        <span className="text-[#94A3B8] text-sm md:text-base sm:pb-2">of this job's tasks currently show AI use</span>
      </div>

      <div className="mt-5 h-2.5 w-full rounded-full bg-white/10 overflow-hidden">
        <motion.div className="h-full rounded-full" style={{ background: color }}
          initial={{ width: 0 }} animate={{ width: `${Math.min(100, job.pct)}%` }} transition={{ duration: 0.7 }} />
      </div>

      <div className="mt-5 inline-flex items-center gap-2 rounded-full px-4 py-1.5 bg-[#00F0FF]/10 border border-[#00F0FF]/30" data-testid="result-rank">
        <TrendingUp className="w-4 h-4 text-[#00F0FF]" />
        {job.pct === 0
          ? <span className="text-sm font-medium">No AI use observed yet — along with ~{Math.max(1, job.total - job.rank) + 1} other occupations</span>
          : <span className="text-sm font-medium">#{job.rank} of {job.total} by AI exposure</span>}
      </div>

      {job.pct === 0 ? (
        <p className="mt-5 text-[#e2e8f0]" data-testid="result-zero-note">
          AI use hasn't shown up in this job's tasks yet. That's an insight in itself — it's one of roughly {Math.max(1, job.total - job.rank) + 1} occupations where <span className="font-bold" style={{ color }}>no AI use has been observed</span> so far.
        </p>
      ) : (
        <p className="mt-5 text-[#e2e8f0]">AI is currently used in <span className="font-bold" style={{ color }}>{job.pct}%</span> of this job's tasks.</p>
      )}

      <div className="mt-3 flex items-start gap-2 rounded-lg bg-[#111827] border border-white/10 px-3.5 py-3" data-testid="framing-note">
        <Info className="w-4 h-4 text-[#94A3B8] mt-0.5 shrink-0" />
        <p className="text-sm text-[#94A3B8]">This measures how AI is used today — not a prediction of job loss.</p>
      </div>

      <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
        <DataCredit />
        <button onClick={onShare} data-testid="share-result-btn"
          className="inline-flex items-center gap-2 bg-[#00F0FF] text-[#04121a] font-semibold px-5 py-2.5 rounded-md hover:brightness-110 transition">
          <Share2 className="w-4 h-4" /> Share my result
        </button>
      </div>
    </motion.div>
  );
}

export default function AIinYourJob() {
  const [meta, setMeta] = useState({ total: 0 });
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [selected, setSelected] = useState(null);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [allJobs, setAllJobs] = useState([]);
  const [browseFilter, setBrowseFilter] = useState("");
  const [sort, setSort] = useState("exposure");
  const [shareJob, setShareJob] = useState(null);
  const cardRef = useRef();

  useEffect(() => {
    api.get("/job-exposure/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (!q) { setResults([]); setSearched(false); return; }
    const t = setTimeout(() => {
      api.get(`/job-exposure/search?q=${encodeURIComponent(q)}`).then((r) => { setResults(r.data); setSearched(true); }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const openBrowse = () => {
    setBrowseOpen(true);
    if (allJobs.length === 0) api.get(`/job-exposure/list?sort=${sort}`).then((r) => setAllJobs(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (browseOpen) api.get(`/job-exposure/list?sort=${sort}`).then((r) => setAllJobs(r.data)).catch(() => {});
  }, [sort, browseOpen]);

  const filteredBrowse = useMemo(() => {
    const f = browseFilter.trim().toLowerCase();
    return f ? allJobs.filter((j) => j.title.toLowerCase().includes(f)) : allJobs;
  }, [allJobs, browseFilter]);

  const select = (job) => { setSelected(job); setResults([]); setQuery(job.title); setBrowseOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const downloadShare = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true });
      const a = document.createElement("a");
      a.download = `ai-in-my-job-${shareJob.title.replace(/\s+/g, "-").toLowerCase()}.png`;
      a.href = dataUrl;
      a.click();
      toast.success("Image downloaded — share it anywhere!");
    } catch (e) { toast.error("Could not generate image"); }
  };

  const shareSocial = (network) => {
    const text = shareJob.pct === 0
      ? `No AI use has been observed yet in tasks for "${shareJob.title}" — one of ~${Math.max(1, shareJob.total - shareJob.rank) + 1} occupations with no observed AI use. See how much AI is doing in your job:`
      : `AI is currently used in ${shareJob.pct}% of tasks for "${shareJob.title}" (ranked #${shareJob.rank} of ${shareJob.total} by AI exposure). See how much AI is doing in your job:`;
    const url = "https://www.worldaiinfo.com/ai-in-your-job";
    const links = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    };
    window.open(links[network], "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-[#0B0F19]" data-testid="ai-in-your-job-page">
      <Seo title="How Much Is AI Doing in Your Job? — Real Data by Occupation | WorldAIInfo" description="Search any occupation to see how much AI is actually used in its tasks today, based on the Anthropic Economic Index. Real data, not predictions." canonicalPath="/ai-in-your-job" />
      <div className="max-w-3xl mx-auto px-6 md:px-12 pt-28 pb-20">
        <div className="inline-flex items-center gap-2 border border-[#00F0FF]/30 rounded-full px-4 py-1.5 mb-6 bg-[#00F0FF]/5">
          <Sparkles className="w-4 h-4 text-[#00F0FF]" />
          <span className="font-mono-tech text-xs text-[#00F0FF]">REAL DATA · {meta.total} JOBS</span>
        </div>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl leading-tight">How Much Is AI Doing in Your Job?</h1>
        <p className="mt-5 text-base md:text-lg text-[#94A3B8] leading-relaxed">
          Search your job title to see the share of its tasks where AI is <span className="text-white font-medium">currently used</span> —
          based on real usage data, not predictions.
        </p>

        {/* Search */}
        <div className="mt-8 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
          <input
            value={query} onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
            placeholder="Type your job title (e.g. Marketing Manager, Nurse, Teacher)…"
            data-testid="job-search-input"
            className="w-full bg-[#0e1424] border border-white/10 rounded-xl pl-12 pr-4 py-4 text-base focus:border-[#00F0FF] focus:outline-none" />
        </div>
        <button onClick={openBrowse} data-testid="browse-jobs-btn"
          className="mt-3 inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#00F0FF] transition">
          <List className="w-4 h-4" /> or browse all {meta.total} jobs
        </button>

        {/* Search suggestions */}
        {!selected && query.trim() && results.length > 0 && (
          <div className="mt-3 card-surface rounded-xl overflow-hidden divide-y divide-white/5" data-testid="search-results">
            {results.map((j) => (
              <button key={j.id} onClick={() => select(j)} data-testid="search-result-item"
                className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-white/[0.04] transition">
                <span className="text-sm">{j.title}</span>
                <span className="text-sm font-mono-tech" style={{ color: pctColor(j.pct) }}>{j.pct}%</span>
              </button>
            ))}
          </div>
        )}

        {/* Not found */}
        {!selected && searched && query.trim() && results.length === 0 && (
          <div className="mt-3 card-surface rounded-xl px-4 py-4 text-sm text-[#94A3B8]" data-testid="not-found-msg">
            We don't have verified data for that exact title yet — try a related one.
          </div>
        )}

        {/* Result */}
        {selected && (
          <div className="mt-8">
            <ResultCard job={selected} onShare={() => setShareJob(selected)} />
          </div>
        )}

        {/* Newsletter capture — after engaging with the explorer */}
        <div className="mt-12">
          <NewsletterSignup variant="inline" source="ai_in_your_job" testid="ai-job-newsletter" />
        </div>
      </div>

      {/* Browse drawer */}
      <AnimatePresence>
        {browseOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-[60]" onClick={() => setBrowseOpen(false)} />
            <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-[#0B0F19] border-l border-white/10 z-[61] flex flex-col"
              data-testid="browse-drawer">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <h3 className="font-display font-bold text-lg">Browse all jobs</h3>
                <button onClick={() => setBrowseOpen(false)} data-testid="browse-close" className="w-8 h-8 rounded-full hover:bg-white/5 flex items-center justify-center"><X className="w-4 h-4" /></button>
              </div>
              <div className="p-4 space-y-3 border-b border-white/10">
                <input value={browseFilter} onChange={(e) => setBrowseFilter(e.target.value)} placeholder="Filter jobs…"
                  data-testid="browse-filter" className="w-full bg-[#0e1424] border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#00F0FF] focus:outline-none" />
                <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="browse-sort"
                  className="w-full bg-[#0e1424] border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#00F0FF] focus:outline-none">
                  <option value="exposure">Highest AI exposure</option>
                  <option value="lowest">Lowest AI exposure</option>
                  <option value="title">A–Z</option>
                </select>
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-white/5">
                {filteredBrowse.map((j) => (
                  <button key={j.id} onClick={() => select(j)} data-testid="browse-item"
                    className="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-white/[0.04] transition">
                    <span className="text-sm min-w-0 truncate">{j.title}</span>
                    <span className="text-xs font-mono-tech shrink-0" style={{ color: pctColor(j.pct) }}>{j.pct}% · #{j.rank}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Share modal */}
      <AnimatePresence>
        {shareJob && (
          <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShareJob(null)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()} className="max-w-[640px] w-full" data-testid="share-modal">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold text-lg text-white">Share your result</h3>
                <button onClick={() => setShareJob(null)} data-testid="share-close" className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white"><X className="w-4 h-4" /></button>
              </div>
              <div className="overflow-x-auto">
                <ShareCard job={shareJob} cardRef={cardRef} />
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <button onClick={downloadShare} data-testid="share-download"
                  className="inline-flex items-center gap-2 bg-[#00F0FF] text-[#04121a] font-semibold px-4 py-2.5 rounded-md hover:brightness-110"><Download className="w-4 h-4" /> Download image</button>
                <button onClick={() => shareSocial("twitter")} data-testid="share-twitter"
                  className="text-sm px-4 py-2.5 rounded-md border border-white/15 text-white hover:border-[#00F0FF]/50">Twitter / X</button>
                <button onClick={() => shareSocial("linkedin")} data-testid="share-linkedin"
                  className="text-sm px-4 py-2.5 rounded-md border border-white/15 text-white hover:border-[#00F0FF]/50">LinkedIn</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
